// common-apiService.js

import axios from "axios";

export const isValidUUID = (uuid) => {
    const uuidRegex = /^[a-f\d]{8}(-[a-f\d]{4}){3}-[a-f\d]{12}$/i;
    return uuidRegex.test(uuid);
};

class ApiError extends Error {
    constructor(message, response) {
        super(message);
        this.name = 'ApiError';
        this.response = response;
    }
}

const formatUtcTimestamp = (timestamp) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const locale = navigator.language || navigator.userLanguage;
    if (!timestamp) return 'N/A';
    const utcDate = new Date(timestamp);
    return new Intl.DateTimeFormat(locale, {
        timeZone: timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(utcDate);
};

const handleResponse = async (response) => {
    if (!response.ok) {
        let errorDetails = `Failed to fetch data from server ${response.status}: ${response.statusText}`;
        try {
            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            if (isJson) {
                const errorBody = await response.json();
                errorDetails += `\nMessage: ${errorBody.message || 'N/A'}`;
                errorDetails += `\nDetails: ${errorBody.extraDetails || 'N/A'}`;
                errorDetails += `\nTimestamp: ${formatUtcTimestamp(errorBody.timestamp)}`;
            }
        } catch (e) {
            console.error('Error parsing error response body:', e);
        }
        console.log(errorDetails);
        throw new ApiError(errorDetails, response);
    }
    // Check if the response is JSON
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    if (isJson) {
        const buffer = await response.arrayBuffer();
        const decoder = new TextDecoder('utf-8');
        const decodedData = decoder.decode(buffer);
        return JSON.parse(decodedData);
    }
    // If it's not JSON, proceed with regular response
    return response;
};

const makeApiRequest = async (url, method, headers = {}, body = null) => {
    try {
        const response = await fetch(url, {
            method,
            headers: {
                ...headers,
            },
            body: body,
        });
        return handleResponse(response);
    } catch (error) {
        console.error(`Error making API request: ${error.message}`);
        throw error;
    }
};

export const makeApiRequest_Text = async (url, method, headers = {}, body = null) => {
    const response = await fetch(url, {
        method,
        headers,
        body,
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.text();
};

export const makeApiRequest_AppJson = async (url, method, headers = {}, body = null) => {
    const appJson_headers = {
        'Content-Type': 'application/json',
        ...headers,
    };
    const appJson_body = body ? JSON.stringify(body) : null;
    return makeApiRequest(url, method, appJson_headers, appJson_body);
};

export const makeApiRequest_formData = async (url, method, headers = {}, body = null) => {
    const formData_headers = {
        ...headers,
    };
    return makeApiRequest(url, method, formData_headers, body);
};

export const makeApiRequest_blobData = async (url, method, headers = {}, body = null) => {
    const blob_headers = {
        'Content-Type': 'application/json',
        ...headers,
    };
    try {
        const response = await fetch(url, {
            method,
            headers: blob_headers,
            body: body ? JSON.stringify(body) : null,
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        return blob;
    } catch (error) {
        console.error(`Error making API request: ${error.message}`);
        throw error;
    }
}


export const generateQueryString = (searchFields) => {
    if (!searchFields) {
        return "";
    }
    return Object.entries(searchFields)
        .filter(([key, value]) => value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
};

export const generateURLSearchParams = (searchFields) => {
    if (!searchFields) {
        return new URLSearchParams();
    }
    const params = new URLSearchParams();
    Object.entries(searchFields)
        .filter(([key, value]) => value !== '')
        .forEach(([key, value]) => params.append(key, value));
    return params;
};

/**
 * Constructs a query string from an object of parameters.
 *
 * @param {Object} params - The parameters to be converted into a query string.
 * @param {string} params.key - The key of the parameter.
 * @param {string|number|boolean} params.value - The value of the parameter.
 * @returns {string} The constructed query string, starting with '?' if there are parameters, otherwise an empty string.
 *
 * @example
 * const params = { search: 'test', page: 2, active: true };
 * const queryString = constructQueryParams(params);
 * console.log(queryString); // Output: "?search=test&page=2&active=true"
 */
export const constructQueryParams = (params) => {
    const queryParams = [];
    for (const key in params) {
        if (params[key] !== null && params[key] !== undefined) {
            queryParams.push(`${key}=${params[key]}`);
        }
    }
    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
};

export const downloadCSVFile = async (url, accessToken, fileName = 'filename.csv') => {
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`
        }
    });
    if (!response.ok)
        throw new Error('Failed to fetch CSV file');
    const data = await response.text();
    const utf8Data = new TextEncoder().encode(data);
    const blob = new Blob([utf8Data], { type: 'text/csv; charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    link.click();
    window.URL.revokeObjectURL(link.href);
};

export const downloadFile = async (accessToken, url, fileName) => {
    try {
        const response = await axios.get(url, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${accessToken}`
            }
        });
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error('Error downloading file:', error);
        throw new Error('Failed to download file');
    }
}